import { api } from "@/lib/api";
import {
  ReservationDto,
  reservationDtoListSchema,
  ReservationDtoSchema,
  reservationFromDto,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

export const getReservationsQueryOptions = queryOptions({
  queryKey: ["reservations"],
  queryFn: async () => {
    const result: Array<ReservationDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.getWithCache("/api/reservations", reservationDtoListSchema, {
        query: {
          page: page.toString(),
        },
      });
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(reservationFromDto);
  },
});

export function useGetReservations<TERROR extends Error>(opts?: {
  onError: (error: TERROR) => void;
}) {
  const { toast } = useToast();

  const q = useQuery(getReservationsQueryOptions);
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getReservationsError",
      variant: "destructive",
    });
    opts?.onError(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export function getReservationQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["reservations", id],
    queryFn: () =>
      api
        .get("/api/reservations/$id", ReservationDtoSchema, {
          pathReplace: { $id: id },
        })
        .then(reservationFromDto),
  });
}

export function useGetReservation<TERROR extends Error>(
  id: string,
  opts?: { onError?: (error: TERROR) => void; enabled?: boolean },
) {
  const { toast } = useToast();
  const q = useQuery({
    ...getReservationQueryOptions(id),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getRegionDetailError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
